"use client";

import { useMediaQuery } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";

const getSavedDevice = (): "mobile" | "desktop" | null => 
{
    if (typeof window === "undefined") return null;

    // Primeiro tenta localStorage
    const saved = localStorage.getItem("device");
    if (saved === "mobile" || saved === "desktop") return saved;

    // Fallback para cookie
    const cookies = document.cookie.split("; ").map((c) => c.split("="));
    const cookie = cookies.find(([ key ]) => key === "device");

    if (cookie && (cookie[1] === "mobile" || cookie[1] === "desktop")) 
    {
        return cookie[1] as "mobile" | "desktop";
    }

    return null;
};

const saveDevice = (device: "mobile" | "desktop") => 
{
    if (typeof window === "undefined") return;
    localStorage.setItem("device", device);
    document.cookie = `device=${device}; path=/; max-age=31536000; SameSite=Lax`;
};

const useResponsive = () => 
{
    const [ mounted, setMounted ] = useState(false);
    const savedDevice = typeof window !== "undefined" ? getSavedDevice() : null;
    
    const [ isMobile, setIsMobile ] = useState(savedDevice === "mobile");
    const [ isDesktop, setIsDesktop ] = useState(savedDevice === "desktop");
    
    const [ mqMobile ] = useMediaQuery([ "(max-width: 991px)" ], { fallback : [ false ] });
    const [ mqDesktop ] = useMediaQuery([ "(min-width: 992px)" ], { fallback : [ true ] });
    
    useEffect(() => 
    {
        setMounted(true);
    
        // Só detecta se não havia device salvo
        if (!savedDevice) 
        {
            const detectedDevice = mqMobile ? "mobile" : "desktop";
            saveDevice(detectedDevice);
            setIsMobile(detectedDevice === "mobile");
            setIsDesktop(detectedDevice === "desktop");
        }
    }, []);
    
    // Atualiza dinamicamente o estado só se mudar de fato (evita loop)
    useEffect(() => 
    {
        if (mounted && savedDevice) 
        {
            if (mqMobile && !isMobile)
            {
                setIsMobile(true);
                setIsDesktop(false);
            }
            else if (mqDesktop && !isDesktop)
            {
                setIsMobile(false);
                setIsDesktop(true);
            }
        }
    }, [ mqMobile, mqDesktop, mounted ]);
    
    return {
        isMobile  : mounted ? isMobile : false,
        isDesktop : mounted ? isDesktop : true,
        isClient  : mounted,
    };
};

// Componentes auxiliares
const Desktop = ({ children }: { children: React.ReactNode }) => 
{
    const { isDesktop } = useResponsive();
    return isDesktop ? { children } : null;
};

const Mobile = ({ children }: { children: React.ReactNode }) => 
{
    const { isMobile } = useResponsive();
    return isMobile ? { children } : null;
};

export { useResponsive, Desktop, Mobile };